<template>
  <div class="comparison-table-wrapper">
    <table class="comparison-table">
      <thead>
        <tr>
          <th class="feature-col"></th>
          <th
            v-for="plan in plans"
            :key="plan.key"
            class="plan-col plan-clickable"
            :class="{ 'plan-recommended': plan.recommended }"
            @click="onHeaderClick(plan.key)"
          >
            <div class="plan-header">
              <div v-if="plan.recommended" class="recommend-banner">{{ $t('Recommended') }}</div>
              <h4>{{ plan.name }}</h4>
              <template v-if="plan.product && plan.product.allowusertobuy">
                <div class="price" v-html="formatPrice(plan.product.price / 12, $t('user/mo'))"></div>
                <div class="price-description">
                  {{ $tc('minimum {count} user | minimum {count} users', plan.product.minimumquantitynew) }},
                  {{ $t('billed anually') }}
                  <Icon
                    v-tooltip="{
                      content: $t(`Starting at ${formatInlinePrice(plan.product.price * plan.product.minimumquantitynew)} USD per year`),
                    }"
                    class="ml-1"
                    name="info-circle"
                    family="fas"
                  />
                </div>
                <span class="btn btn-outline-primary btn-sm plan-btn mt-3 mb-2">{{ $t('Buy') }}</span>
              </template>
              <template v-else>
                <div class="price-description quote">{{ $t('Custom pricing available') }}</div>
                <span class="btn btn-outline-primary btn-sm plan-btn mt-3 mb-2">{{ $t('Get Quote') }}</span>
              </template>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(label, index) in featureLabels" :key="label">
          <td class="feature-col">{{ $t(label) }}</td>
          <td
            v-for="plan in plans"
            :key="plan.key"
            class="plan-col"
            :class="{ 'plan-recommended': plan.recommended }"
          >
            <template v-if="plan.benefits[index].value === true">
              <Icon name="check" family="far" class="icon-check" />
            </template>
            <template v-else-if="plan.benefits[index].value === false">
              <Icon name="times" family="far" class="icon-times" />
            </template>
            <template v-else>
              {{ plan.benefits[index].value }}
            </template>
          </td>
        </tr>
        <tr class="more-row">
          <td class="feature-col"></td>
          <td
            v-for="plan in plans"
            :key="plan.key"
            class="plan-col"
          >
            <a class="btn btn-link more-link" href="https://www.getfilecloud.com/pricing/" target="_blank">{{ $t('More...') }}</a>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import Icon from 'common/components/Icon.vue';
import { formatPrice, formatInlinePrice } from '@/utils/checkout';

export default {
  name: 'ComparisonTable',
  components: {
    Icon
  },
  props: {
    plans: {
      type: Array,
      required: true
    },
    featureLabels: {
      type: Array,
      required: true
    }
  },
  methods: {
    formatPrice,
    formatInlinePrice,
    onHeaderClick(key) {
      this.$emit('select', key);
    }
  }
};
</script>

<style lang="scss" scoped>
.comparison-table-wrapper {
  overflow-x: auto;
  margin-top: -2rem;
}

.comparison-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.75rem;

  thead {
    th {
      vertical-align: bottom;
      padding: 0 0.5rem 0.75rem;
      border-bottom: 1px solid var(--border-color);
      font-size: 0.875rem;

      &.feature-col {
        width: 40%;
      }

      &.plan-clickable {
        padding: 0 0.375rem 0.75rem;
        cursor: pointer;
        overflow: visible;

        .plan-header {
          border: 1px solid var(--border-color);
          border-radius: 0.375rem;
          padding: 1.25rem 1rem;
          overflow: visible;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        &:hover .plan-header {
          border-color: var(--color-primary);
          box-shadow: 0 2px 12px rgba(0, 103, 173, 0.1);

          .plan-btn {
            background-color: var(--color-primary);
            color: var(--color-primary--foreground, #fff);
          }
        }

        &.plan-recommended {
          padding-top: 0.75rem;

          .plan-header {
            border: 2px solid var(--color-primary);
            padding-top: 2rem;
          }
        }

        &.plan-recommended:hover .plan-header {
          box-shadow: 0 4px 20px rgba(0, 103, 173, 0.18);
        }
      }
    }
  }

  tbody {
    tr {
      &:nth-child(even) {
        background: var(--bg-medium);
      }

      td {
        padding: 0.5rem;
        border-bottom: 1px solid var(--border-color);
        vertical-align: middle;

        &.feature-col {
          width: 40%;
          text-align: left;
          color: var(--text-dark);
          font-weight: 500;
        }

        &.plan-col {
          text-align: center;
        }
      }
    }
  }
}

.plan-header {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  justify-content: flex-end;
  background: transparent;

  h4 {
    margin: 0 0 0.5rem;
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--text-dark);
  }
}

.plan-btn {
  padding: 0.3rem 2rem;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.688rem;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.recommend-banner {
  position: absolute;
  top: -0.625rem;
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-primary);
  color: #fff;
  padding: 0.15rem 0.875rem;
  border-radius: 10rem;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
  letter-spacing: 0.03em;
}

.price {
  font-size: 1.5rem;
  color: var(--color-primary);
  font-weight: 700;
  line-height: 1.2;

  ::v-deep small {
    font-size: 0.5rem;
    font-weight: 700;
    position: relative;
    bottom: 10px;
    margin-right: 0.2rem;

    &.suffix {
      bottom: 0;
      font-size: 0.75rem;
      margin-left: 0.2rem;
      font-weight: 400;
    }
  }
}

.price-description {
  font-size: 0.625rem;
  color: var(--text-light, var(--text-medium));
  text-align: center;

  &.quote {
    font-size: 0.75rem;
    color: var(--text-dark);
    margin-top: 0.25rem;
  }
}

.icon-check {
  color: var(--color-primary);
  font-size: 0.875rem;
}

.icon-times {
  color: var(--danger, #dc3545);
  font-size: 0.875rem;
}

.more-row td {
  padding: 0.25rem 0.5rem;
  border-bottom: none;
}

.more-link {
  font-size: 0.75rem;
}
</style>
